import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useHistory, useParams } from 'react-router';
import { PiArrowUpDuotone, PiArrowDownDuotone, PiArrowLeftDuotone, PiXDuotone, PiPlusDuotone, PiWarningDuotone } from 'react-icons/pi';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  Alert,
  BlockingOverlay,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from 'src/components/ui';
import EmptyState from 'src/components/Icons/EmptyStates/EmptyState';
import { AccordionItem } from 'src/components/ui/Accordion';
import classnames from 'src/util/classnames';
import useResourceLoader from '../../util/useResourceLoader';
import InventoryScheduleLoadingSkeleton from './InventoryScheduleLoadingSkeleton';
import InventoryScheduleApi, {
  InventoryColumn,
  InventoryColumnFingerprint,
  InventoryColumnPlan,
  InventorySchedule,
  InventorySchedulePlan,
  InventoryScheduleVersion,
  MAX_VIEW_NAME_LENGTH,
} from '../../api/InventoryScheduleApi';

// Declared column types, in the order the legacy RJSF dropdown used, so the UI reads identically to
// the old workpage form.
//
// Inventory offers only UNIT-LESS types. Whether a column holds dollars or a rate is said by its
// TEMPLATE FORMAT, not its type — see FORMATS_BY_TYPE below and TypeToVariableType in
// InventoryScheduleService. The unit-bearing types (AMOUNT, AMOUNT_UNITS, PERCENTAGE) belong to the
// financial schedules and are deliberately not offered here.
const TYPE_VALUES = ['integer', 'number', 'date', 'text'];

// The :id route segment that means "create a new schedule" rather than naming an existing one — the same
// sentinel other system-settings edit pages use for their own :id-shaped routes (see OrgUnitEditorPage).
const NEW_ROUTE_ID = 'new';

// Which template formats each type can actually render — the OPINIONATED half of the rule the backend
// enforces (InventoryScheduleService.FormatsByType). The column definition itself is deliberately
// decoupled from this, so a format can be changed without retyping the column; the pairing lives here
// and at the API boundary, which is what keeps an unusable combination from ever being offered.
// A type absent from this map takes no format: 'text' has exactly one presentation.
// Every inventory type is unit-less, so this is where a column's unit is actually decided: '$0.00'
// prints money, '0.00%' prints a rate, '0.00' prints a bare number. That is the whole reason
// inventory uses unit-less types — the unit is not reliably knowable per column at definition time,
// and here it is one dropdown and free to change. Mirrors FormatsByType in InventoryScheduleService.
const FORMATS_BY_TYPE: Record<string, string[]> = {
  number: [
    'Decimal0',
    'Decimal2',
    'Decimal4',
    'DollarWhole',
    'DollarDecimal2',
    'PercentWhole',
    'PercentDecimal2',
  ],
  // Whole-number variants only: a fixed decimal count contradicts the declared type, whichever unit
  // it is wearing.
  integer: ['Decimal0', 'DollarWhole', 'PercentWhole'],
  date: ['DateIso', 'DateUs', 'DateIsoSlash'],
};

const formatsFor = (type: string | undefined): string[] => FORMATS_BY_TYPE[type ?? ''] ?? [];

// Resolved at CALL time rather than in a module-level constant, so the label follows the locale that is
// active when it renders — a t`` evaluated at module scope bakes in whatever was active on first import.
const typeLabel = (value: string): string => {
  switch (value) {
    case 'integer':
      return t`Whole number`;
    case 'number':
      return t`Decimal number`;
    case 'date':
      return t`Date`;
    case 'text':
      return t`Text`;
    default:
      return value;
  }
};

// Same call-time rule as typeLabel. The date layouts show the pattern itself rather than a name: the
// admin is choosing what the filer will see in the cell, and "MM/dd/yyyy" says that better than "US".
const formatLabel = (value: string): string => {
  switch (value) {
    // Not translated, and not the word "default": punctuation reads the same in every locale, and it
    // matches the placeholder the column-type select above already uses for "nothing chosen".
    case 'None':
      return '---';
    case 'Decimal0':
      return '0';
    case 'Decimal2':
      return '0.00';
    case 'Decimal4':
      return '0.0000';
    case 'DollarWhole':
      return '$0';
    case 'DollarDecimal2':
      return '$0.00';
    case 'PercentWhole':
      return '0%';
    case 'PercentDecimal2':
      return '0.00%';
    case 'DateIso':
      return 'yyyy-MM-dd';
    case 'DateUs':
      return 'MM/dd/yyyy';
    case 'DateIsoSlash':
      return 'yyyy/MM/dd';
    default:
      return value;
  }
};

// A save whose plan carries warnings or retirements needs confirmation; a retire always confirms.
// The plan comes from the BACKEND (POST inventory-schedules/preview) — this component used to diff the
// edited schedule against the loaded one itself, which could not see revivals and did not gate renames,
// so a wholesale re-layout of CML relabeled 11 data-bearing columns with no dialog at all.
type ConfirmState =
  | { kind: 'save'; plan: InventorySchedulePlan }
  | { kind: 'retire' }
  | null;

// What a save would do to a view that ALREADY EXISTS: rename it, or drop it (the name was cleared).
// Null when nothing changes, and always null for a new schedule — there is no view yet to desync.
type ViewNameChange = { kind: 'renamed' | 'cleared'; from: string; to: string | null };

// Superset binds a dataset to a view BY NAME, and nothing repoints it when the name changes: the old
// view is dropped on save, so every dataset, chart and dashboard built on it breaks silently. A rename
// buys nothing functionally — the schedule, its uploads and its data work the same under any name — so
// this is loud on purpose. Shown inline under the field AND at the top of the save confirmation.
function ViewNameChangeWarning({ change }: { change: ViewNameChange }) {
  const oldView = `ocif_analytics_${change.from}`;
  const oldPrelim = `${oldView}_prelim`;
  return (
    <Alert color="danger" className="d-flex gap-2 align-items-start">
      <AlertTriangle size={22} className="shrink-0 mt-1" aria-hidden />
      <div>
        {change.kind === 'renamed' ? (
          <>
            <strong>
              <Trans>Renaming this view will break its Superset datasets.</Trans>
            </strong>
            <div className="mt-1">
              <Trans>
                Renaming is not needed for anything to work: the schedule, its uploads and its data behave
                the same under any view name. On save, «{oldView}» and «{oldPrelim}» are dropped, and every
                Superset dataset, chart and dashboard built on them stops working until it is repointed in
                Superset by hand.
              </Trans>
            </div>
          </>
        ) : (
          <>
            <strong>
              <Trans>Clearing the view name drops its views and breaks their Superset datasets.</Trans>
            </strong>
            <div className="mt-1">
              <Trans>
                On save, «{oldView}» and «{oldPrelim}» are dropped, and every Superset dataset, chart and
                dashboard built on them stops working.
              </Trans>
            </div>
          </>
        )}
      </div>
    </Alert>
  );
}

// The same comparison key the backend matches headers on (InventoryScheduleService.NormalizeHeader):
// case-folded, diacritics stripped, whitespace runs collapsed. Used ONLY to reject locally what the
// backend would 400 on — never to predict which stored column a header will resolve to. That decision is
// the backend's, and asking it is what preview() is for.
const normalizeHeader = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/\s+/g, ' ')
    .normalize('NFC');

// EDITOR-LOCAL state, deliberately not part of the wire shape. `detachedFrom` remembers the binding a
// type change gave up, so the notice on that row can offer to put it back. toPayload builds each column
// field by field, so nothing in here can leak into a request.
type DetachedFrom = { variableId: string | null; type: string };
type EditorColumn = InventoryColumn & { detachedFrom?: DetachedFrom };
type EditorSchedule = Omit<InventorySchedule, 'columns'> & { columns: EditorColumn[] };

// A column the user just added is, by construction, a NEW one. Saying so at the moment of creation is
// the whole trick: a row's identity is fixed when it comes into existence and never drifts afterwards.
const newColumn = (order: number): EditorColumn => ({
  binding: 'new',
  id: null,
  name: '',
  type: 'text',
  order,
});

// Postgres's identifier limit, in BYTES (InventoryScheduleService.MAX_VIEW_COLUMN_NAME_BYTES). An accented
// name is longer in bytes than it looks.
const MAX_VIEW_COLUMN_NAME_BYTES = 63;

const blankSchedule = (): EditorSchedule => ({
  fireId: '',
  scheduleType: '',
  title: '',
  viewName: '',
  columns: [newColumn(0)],
  legacyColumns: [],
});

// A column is RESTRICTED to a set of accepted values when it carries an array — an EMPTY array still
// counts (the checkbox is on, the list just isn't filled in yet, which validate() blocks on save).
// Unrestricted columns carry null/undefined instead.
const isRestricted = (c: InventoryColumn): boolean => Array.isArray(c.acceptedValues);

// The exact body both preview and save send. Factored out precisely so the plan the user is shown is
// computed from the SAME bytes the save posts — a preview of a different payload would be a lie.
const toPayload = (def: InventorySchedule): InventorySchedule => ({
  ...def,
  scheduleType: def.scheduleType?.trim() || null,
  viewName: def.viewName?.trim() || null,
  // null stays null (no assertion); a list is the full set to show. A column the layout CONTINUES is live
  // again (a revival), and the backend refuses to show a live column as historical — so it is dropped
  // here, which is also what the backend does to the stored flag when a column comes back.
  legacyColumns: def.legacyColumns
    ? def.legacyColumns
        .filter((l) => !def.columns.some((c) => c.binding === 'continue' && c.id === l.id))
        .map((l) => ({ id: l.id, name: l.name.trim() }))
    : null,
  columns: def.columns.map((c, i) => ({
    // Stated, not implied. A column carrying an id used to be enough for the backend to treat it as a
    // rename, which meant the id had to ride along invisibly and a header edit could silently rebind
    // someone else's data. Now the row says which it is, and an id without 'continue' means nothing.
    binding: c.binding ?? 'new',
    id: c.binding === 'continue' ? c.id ?? null : null,
    name: c.name.trim(),
    type: c.type,
    order: i,
    // Authoritative, like acceptedValues below: what is sent replaces what is stored. Normalized to
    // 'None' rather than left undefined so a cleared format is an assertion the backend applies —
    // and so this object never omits the key, which is what the import path reads as "no assertion".
    //
    // Sent only when the type can carry it. A format the type cannot render is rejected by the API
    // (FormatsByType), and a stale one can outlive a retype in local editing state — changeColumnType
    // resets it, but a definition loaded with one already invalid would otherwise fail the save with
    // an error about a format the user never picked.
    format: formatsFor(c.type).includes(c.format ?? '') ? c.format : 'None',
    // Authoritative: the list replaces the stored set, null clears any restriction.
    acceptedValues: isRestricted(c) ? (c.acceptedValues ?? []).map((v) => v.trim()) : null,
  })),
});

// How each planned outcome presents in the column grid: a short label and a bootstrap contextual colour.
// `warning` outranks the action — a rename that carries someone else's history is the case this whole
// affordance exists for, so it must not read as the same routine grey as a rename of an empty column.
const planBadge = (
  p: InventoryColumnPlan
): { label: string; color: string; title: string } => {
  // Aliased into plain identifiers so the extracted messages carry NAMED placeholders ({fromHeader},
  // {count}) rather than positional {0}/{1} — a member expression is all Lingui can name positionally,
  // which leaves a translator guessing which number is which.
  const fromHeader = p.fromHeader ?? '';
  const count = p.dataPoints;

  if (p.warning === 'CARRIES_UNRELATED_HISTORY') {
    return {
      label: t`Takes over data`,
      color: 'danger',
      title: t`This column reuses the identity of «${fromHeader}». Its ${count} filed values will be relabeled under this name.`,
    };
  }
  switch (p.action) {
    case 'keep':
      return { label: t`Unchanged`, color: 'secondary', title: t`Already defined; nothing moves.` };
    case 'revive':
      return {
        label: t`Restored`,
        color: 'success',
        title: t`This column was retired. Saving brings it back along with its ${count} filed values.`,
      };
    case 'rename':
      return {
        label: t`Renamed`,
        color: 'info',
        title: t`Renamed from «${fromHeader}». It has no filed data, so nothing moves.`,
      };
    default:
      return { label: t`New`, color: 'info', title: t`A brand-new column, with no data.` };
  }
};

// A column's filing record in one line: "412 values · 14 periods (2022-Q4 → 2026-Q2) · 31 institutions".
// This is what makes a binding recognisable — far more so than any id — so it is built from whatever the
// fingerprint actually has and simply gets shorter when a column has never been filed against.
const filingSummary = (f: InventoryColumnFingerprint): string => {
  if (f.dataPoints === 0) {
    return t`no data filed`;
  }
  const parts = [t`${f.dataPoints} values`];
  if (f.periods > 0) {
    parts.push(
      f.firstPeriodLabel && f.lastPeriodLabel && f.firstPeriodLabel !== f.lastPeriodLabel
        ? t`${f.periods} periods (${f.firstPeriodLabel} → ${f.lastPeriodLabel})`
        : t`${f.periods} periods`
    );
  }
  if (f.institutions > 0) {
    parts.push(t`${f.institutions} institutions`);
  }
  return parts.join(' · ');
};

// A RETIRED column whose name this new column is reusing.
//
// Making revival explicit closed one trap and opened another. Retyping a retired column's name used to
// bring it back automatically — a footgun, because it happened silently, but it did mean you got your
// data back. Now that same gesture produces a new, EMPTY column while the filed values stay stranded on
// the retired one, and nothing rejects it: there is no index on the header, and the duplicate-name check
// only ever compares the posted layout against itself. So this warning is the only thing between the
// user and a stranded column.
//
// Matches former names too: someone re-adding «Fecha de Vencimiento» should be told about it even if the
// retired column has since been renamed to something else.
//
// A WARNING, never a block — reusing a retired name is legitimate (a genuinely new column that happens to
// reuse wording). The user decides; they just must not decide it by accident.
const retiredNameMatch = (
  name: string,
  all: Map<string, InventoryColumnFingerprint>,
  claimed: Set<string>
): InventoryColumnFingerprint | null => {
  const key = normalizeHeader(name);
  if (!key) {
    return null;
  }
  for (const f of all.values()) {
    if (!f.retired || claimed.has(f.variableId)) {
      continue;
    }
    if (
      normalizeHeader(f.currentHeader ?? '') === key ||
      f.formerHeaders.some((h) => normalizeHeader(h) === key)
    ) {
      return f;
    }
  }
  return null;
};

// Header keys used by more than one column in the layout. The backend rejects these outright (the
// generated view names its columns after the header, so two that fold together produce a view nobody can
// read), but validate() only runs on save — and in the meantime the preview 400s and every "On save"
// badge silently vanishes. Computing it here lets the offending rows say so as they are typed.
const duplicateHeaderKeys = (columns: EditorColumn[]): Set<string> => {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const c of columns) {
    const key = normalizeHeader(c.name ?? '');
    if (!key) {
      continue;
    }
    if (seen.has(key)) {
      dupes.add(key);
    }
    seen.add(key);
  }
  return dupes;
};

// A restore point's timestamp, in the reader's own locale. The backend sends UTC; showing that raw
// would ask someone to work out whether "13:37Z" is the save they remember making after lunch.
const formatVersionStamp = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

// What restoring a version would cost, in the same voice as a column's filing summary: the consequence
// first, the count second. Returns null when a version matches the current layout exactly — there is
// nothing to say about restoring what is already there.
const versionDelta = (v: InventoryScheduleVersion): string | null => {
  const parts: string[] = [];
  if (v.restores > 0) {
    parts.push(t`${v.restores} come back`);
  }
  if (v.retires > 0) {
    parts.push(t`${v.retires} retired`);
  }
  if (v.renames > 0) {
    parts.push(t`${v.renames} renamed`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
};

// An icon-only button, as a bare <button> rather than the shared Button.
//
// The shared one carries .btn/.btn-sm, which give an icon-only button a box roughly twice the icon's
// height, and an inline SVG inside it aligns to that box's TEXT BASELINE — so the glyph renders low,
// below the field it sits next to. Aligning it INSIDE the button cannot fix that, because the box
// itself is the wrong size; d-flex, align-items-center and lh-1 were all tried and none won against
// .btn. Dropping the library classes and sizing the button to its own content with inline styles is
// what actually works (line-height:0 plus inline-flex means the height IS the icon's height).
//
// Defined once so every icon button in this editor behaves the same, instead of the same bug being
// re-fixed per call site — which is how it survived in four places while being patched in one.
const IconButton = ({
  label,
  onClick,
  disabled,
  color,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  color?: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    // Equal padding on every side plus flex-centering keeps the icon dead-centered regardless of the
    // box's size, which is what actually avoids the old baseline-misalignment bug — that one came from
    // `.btn`'s line-height sizing the box around TEXT metrics, not from the button having any padding at
    // all. `rounded` + `hover:bg-*` alone (no visible border/background at rest) is what turns this from
    // a bare floating glyph into something that reads as a clickable control on hover.
    className="rounded hover:bg-gray-100 dark:hover:bg-zinc-700"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 0,
      padding: '3px',
      margin: 0,
      lineHeight: 0,
      color: color ?? 'inherit',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
    }}
  >
    {children}
  </button>
);

// A field label for the space INSIDE a column's accordion body — small, muted, uppercase, distinct from
// `.control-label` (bold, full body size) used by the schedule-level fields above the columns list. That
// heavier style is right for three top-level fields; repeated for every field of every column it is what
// made the expanded body read as a wall of same-weight text.
const FieldLabel = ({ children }: { children: ReactNode }) => (
  <div
    className="text-muted"
    style={{
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      marginBottom: '0.375rem',
    }}
  >
    {children}
  </div>
);

// Does this fingerprint match what someone typed into the picker's filter? Matches on the CURRENT header
// and on every former one, so a column can be found by a name it no longer carries — which is exactly the
// position anyone re-pointing CML's columns is in.
const fingerprintMatches = (f: InventoryColumnFingerprint, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    (f.currentHeader ?? '').toLowerCase().includes(q) ||
    f.formerHeaders.some((h) => h.toLowerCase().includes(q))
  );
};

// Deep clone so editing never mutates the loaded list in place (and column ids are preserved verbatim
// — critical: the backend uses a column's id to detect a RENAME and keep its historical facts).
// scheduleType/viewName are carried through even though they have no visible input: the legacy UI only
// exposed Título + Fire ID + Columnas, so we round-trip the loaded metadata untouched instead of
// clobbering it on save.
const cloneSchedule = (s: InventorySchedule): EditorSchedule => ({
  ...s,
  columns: (s.columns ?? []).map((c) => ({
    ...c,
    // Every stored column is one the editor is CONTINUING. The backend states this on the way out, but
    // defaulting here too means a row can never end up unbound just because a read predated the field.
    binding: c.binding ?? ('continue' as const),
    // The accepted-value list is edited in place-ish (every patch rebuilds it), so copy the array too and
    // keep null/undefined as-is — the distinction IS the restricted flag.
    acceptedValues: Array.isArray(c.acceptedValues) ? [...c.acceptedValues] : c.acceptedValues,
  })),
  legacyColumns: s.legacyColumns ? s.legacyColumns.map((l) => ({ ...l })) : s.legacyColumns,
});

interface Props {
  element?: {
    title?: string;
    [':className']?: string;
  };
}

function InventoryScheduleManager({ element }: Props) {
  // The route is /system-settings/:setting/:id — :setting is the workpage slug this instance happens to
  // be mounted under (e.g. 'define-inventory-schedules'), :id is either a schedule's fireId, the literal
  // 'new', or absent (the list view). Read from the URL rather than kept as local state, so a direct link
  // to /system-settings/<slug>/<fireId> opens straight to that schedule's editor.
  const { setting, id: routeId } = useParams<{ setting?: string; id?: string }>();
  const history = useHistory();
  const basePath = setting ? `/system-settings/${setting}` : '/system-settings';

  const isNew = routeId === NEW_ROUTE_ID;
  const selectedFireId = !isNew && routeId ? routeId : null;

  const [refreshKey, setRefreshKey] = useState(0);
  const [schedules, loading, loadError] = useResourceLoader<InventorySchedule[]>(
    (ac) => InventoryScheduleApi.list(ac, { useCache: true }),
    [refreshKey],
    []
  );

  const [editing, setEditing] = useState<EditorSchedule | null>(null);

  const [saving, setSaving] = useState(false);
  // The REAL lock behind `saving`. State only takes effect on the next render, so a double click (or a
  // click on "Save changes" while the button is still enabled) would start a second write that steps on
  // the first. The ref flips synchronously; `saving` mirrors it for the UI.
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  // Backend verdict for the layout as currently edited: what each column will become and how much data
  // moves with it. Null while unavailable (a new schedule, an incomplete layout, or in flight).
  const [plan, setPlan] = useState<InventorySchedulePlan | null>(null);
  // Every stored column of the selected schedule, live and retired, keyed by the id a row binds to.
  // Fetched ONCE per schedule — it describes stored state, so editing cannot change it — and it is what
  // lets a binding be shown as "412 values across 14 periods, formerly Tipo de Interes" rather than as a
  // number nobody can place.
  const [fingerprints, setFingerprints] = useState<Map<string, InventoryColumnFingerprint>>(new Map());
  // WHICH schedule the map above describes, or null when nothing is loaded. The map alone cannot say:
  // an empty one means "still loading", "the fetch failed" and "this schedule has no columns" all at
  // once, and those must not be treated alike — see fingerprintsReady.
  const [fingerprintsFireId, setFingerprintsFireId] = useState<string | null>(null);
  // Which row's binding picker is open, by column index; null when none is.
  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const [pickerQuery, setPickerQuery] = useState('');
  // The schedule-scope counterpart of the binding picker: restore points, opened on demand and fetched
  // then rather than with the schedule, since most sessions never open it.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<InventoryScheduleVersion[] | null>(null);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  // Each column is its own collapsible AccordionItem, open by default only while it has no name (a
  // freshly-added row, or one loaded that way) so the layout isn't a wall of expanded fields the moment a
  // schedule opens. Keyed by index, same as `pickerFor` above — an explicit entry here overrides that
  // default once a row has been toggled by hand.
  const [columnOpenOverrides, setColumnOpenOverrides] = useState<Record<number, boolean>>({});

  const list = useMemo(() => schedules ?? [], [schedules]);

  // Recomputed per keystroke, but it is a fold over a handful of strings — cheap next to the round trip
  // the preview makes, and the whole point is that it does NOT wait for one.
  const dupeKeys = useMemo(
    () => duplicateHeaderKeys(editing?.columns ?? []),
    [editing?.columns]
  );

  // Compared against the SERVER's copy (the loaded list), not anything the editor holds, so a view that
  // exists is recognised however the field was edited — and a restored history version that carries a
  // different name is caught too.
  const storedViewName = useMemo(() => {
    if (isNew || !selectedFireId) {
      return null;
    }
    return list.find((s) => s.fireId === selectedFireId)?.viewName?.trim() || null;
  }, [isNew, selectedFireId, list]);
  const editedViewName = editing?.viewName?.trim() || null;
  const viewNameChange: ViewNameChange | null =
    storedViewName && editedViewName !== storedViewName
      ? { kind: editedViewName ? 'renamed' : 'cleared', from: storedViewName, to: editedViewName }
      : null;

  // Keeps `editing` in step with the route: a blank draft for 'new', the matching stored schedule once
  // the list has loaded, or nothing at all in the list view. Re-runs whenever the list reloads (after a
  // save or a retire) too, so the editor ends up showing the server's own account of what was just
  // written rather than holding on to its own optimistic copy.
  useEffect(() => {
    if (isNew) {
      setEditing(blankSchedule());
      return;
    }
    if (!selectedFireId) {
      setEditing(null);
      return;
    }
    const target = list.find((s) => s.fireId === selectedFireId);
    setEditing(target ? cloneSchedule(target) : null);
  }, [selectedFireId, isNew, schedules]); // eslint-disable-line react-hooks/exhaustive-deps

  // Forgets which columns were expanded/collapsed by hand whenever a DIFFERENT schedule is opened —
  // otherwise row 3's remembered state would apply to whatever row 3 happens to be in the next schedule.
  // Deliberately not keyed on `schedules`, so a save/retire's reload does not reset the layout the user is
  // looking at mid-edit.
  useEffect(() => {
    setColumnOpenOverrides({});
  }, [selectedFireId, isNew]);

  const reload = () => setRefreshKey((k) => k + 1);

  const beginBusy = () => {
    if (busyRef.current) {
      return false;
    }
    busyRef.current = true;
    setSaving(true);
    return true;
  };

  const endBusy = () => {
    busyRef.current = false;
    setSaving(false);
  };

  // No leaving while a write is in flight. Leaving does not cancel the request, so coming back (or
  // opening another schedule) and saving again would race it. The BlockingOverlay below stops every
  // click in the app; this covers what it cannot — the browser's back/forward buttons and closing or
  // reloading the tab. Blocked outright rather than asked: a "leave anyway?" would reopen exactly that
  // race. Reads the ref, not `saving`, so the save's own navigation (after a create or retire) goes
  // through the moment endBusy runs, without a re-render.
  useEffect(() => {
    const unblock = history.block(() => (busyRef.current ? false : undefined));
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (busyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      unblock();
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [history]);

  // Keep the per-column verdict live as the layout is edited, so a rename's consequence is visible WHILE
  // it is being typed rather than only in a dialog at the end. Debounced (a keystroke per column header
  // would otherwise be a request each) and abortable, so a stale response can never overwrite a newer one.
  //
  // Skipped for a new schedule (no history to reconcile against, and its FireID may not resolve yet) and
  // while any header is blank — the endpoint runs the PUT's validation, so an incomplete layout would only
  // come back as a 400.
  const previewSignature = editing
    ? JSON.stringify({
        f: editing.fireId,
        // The binding is part of the signature: rebinding a row changes the outcome without touching its
        // header or type, and leaving it out would leave the previous verdict on screen.
        c: editing.columns.map((c) => [c.binding ?? null, c.id ?? null, c.name.trim(), c.type]),
      })
    : null;

  useEffect(() => {
    if (isNew || !editing || !editing.fireId.trim() || editing.columns.length === 0) {
      setPlan(null);
      return undefined;
    }
    if (editing.columns.some((c) => !c.name.trim())) {
      setPlan(null);
      return undefined;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      InventoryScheduleApi.preview(toPayload(editing), ac)
        .then(setPlan)
        // A failed projection must not surface as an editing error — the save path re-runs it and reports
        // properly. Dropping the stale plan is enough: the UI then shows nothing rather than something wrong.
        .catch(() => setPlan(null));
    }, 400);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [previewSignature, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  // The stored columns behind this schedule. Loaded once per schedule, NOT per edit: nothing the user
  // types changes what has already been filed, and the period/institution figures are far too expensive
  // to recompute on a keystroke. A failure here is not an editing error — the binding lines simply fall
  // back to naming the column, which is what the grid already shows.
  useEffect(() => {
    // Drop the previous schedule's columns FIRST, before anything can be checked against them. Holding
    // them through the fetch means this schedule's rows are validated against another one's inventory,
    // and every single row reads as "the column this continued no longer exists".
    setFingerprints(new Map());
    setFingerprintsFireId(null);
    if (isNew || !selectedFireId) {
      return undefined;
    }
    const ac = new AbortController();
    InventoryScheduleApi.columns(selectedFireId, ac)
      .then((cols) => {
        setFingerprints(new Map(cols.map((c) => [c.variableId, c])));
        // Stamped only on success, and only with the schedule actually fetched.
        setFingerprintsFireId(selectedFireId);
      })
      // Left UNLOADED on failure, deliberately. A binding must never be reported missing because we
      // could not ask — that is an accusation the user can neither verify nor act on. Better to lose
      // the binding detail (the row falls back to naming the column) than to invent a problem.
      .catch(() => undefined);
    return () => ac.abort();
  }, [selectedFireId, isNew, refreshKey]);

  // Are the loaded fingerprints the ones for the schedule on screen? Everything that reasons about a
  // binding being ABSENT has to gate on this. Absence is only meaningful once we have the real answer;
  // until then it is indistinguishable from not having asked yet.
  const fingerprintsReady = !!selectedFireId && fingerprintsFireId === selectedFireId;

  // Dismissing the binding picker. It opens by itself when a column is added, so leaving it with no way
  // out except choosing something would make an inline panel behave like a modal that has taken the
  // page hostage.
  //
  // Deliberately NOT onBlur: the panel holds a search box and a list of buttons, so focus moving
  // BETWEEN its own children fires blur and would close it mid-use.
  //
  // The ref goes on the whole binding block, trigger included, not just the panel. Were it on the panel
  // alone, clicking the trigger to close would fire this first — dismissing — and then the button's
  // onClick would toggle it straight back open.
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (pickerFor === null) {
      return undefined;
    }
    const onPointerDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerFor(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPickerFor(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pickerFor]);

  // Restore points, fetched when the panel is opened rather than with the schedule — most sessions
  // never look at them, and the list costs a scan of the whole history table.
  useEffect(() => {
    if (!historyOpen || isNew || !selectedFireId) {
      return undefined;
    }
    const ac = new AbortController();
    setVersions(null);
    setVersionsError(null);
    InventoryScheduleApi.history(selectedFireId, ac)
      .then(setVersions)
      .catch((e: any) => setVersionsError(e?.message ?? t`Could not load the history.`));
    return () => ac.abort();
  }, [historyOpen, selectedFireId, isNew, refreshKey]);

  // Load an old layout into the EDITOR. Deliberately not a save: it drops the historical definition in
  // as the current edit, so the same preview, the same per-row badges and the same confirmation dialog
  // then describe the restore exactly as they would describe hand editing — and the user can adjust it
  // before committing. Saving is what makes it a new version in the history.
  const loadVersion = async (v: InventoryScheduleVersion) => {
    if (!selectedFireId) {
      return;
    }
    setError(null);
    try {
      const def = await InventoryScheduleApi.historyDefinition(selectedFireId, v.at);
      // A restore rewinds the LAYOUT, not the historical columns (the backend sends none), so the ones on
      // screen carry over — toPayload drops any the restored layout brings back as live.
      setEditing((cur) => ({ ...cloneSchedule(def), legacyColumns: cur?.legacyColumns ?? null }));
      setHistoryOpen(false);
      setPickerFor(null);
      setNotice(
        t`Loaded the layout from ${formatVersionStamp(v.at)}. Nothing is saved yet — review the effects, then save.`
      );
    } catch (e: any) {
      setError(e?.message ?? t`Could not load that version.`);
    }
  };

  // Navigates to the schedule's own edit URL — the route-driven effect above then loads it into `editing`
  // once it lands, which is also what makes a direct link to that URL work identically.
  const selectSchedule = (s: InventorySchedule) => {
    setError(null);
    setNotice(null);
    setPickerFor(null);
    setHistoryOpen(false);
    setVersions(null);
    history.push(`${basePath}/${encodeURIComponent(s.fireId)}`);
  };

  const addSchedule = () => {
    setError(null);
    setNotice(null);
    history.push(`${basePath}/${NEW_ROUTE_ID}`);
  };

  const goToList = () => {
    setError(null);
    setNotice(null);
    setPickerFor(null);
    setHistoryOpen(false);
    setVersions(null);
    history.push(basePath);
  };

  const patch = (changes: Partial<InventorySchedule>) => {
    setEditing((cur) => (cur ? { ...cur, ...changes } : cur));
  };

  // The retired columns that could be shown as historical: every retired stored column the layout is not
  // continuing right now (continuing one revives it, which makes it live), plus any already shown that the
  // fingerprints don't describe — so a shown column never silently drops off the list.
  const historicalCandidates = useMemo(() => {
    const continued = new Set(
      (editing?.columns ?? []).filter((c) => c.binding === 'continue' && c.id).map((c) => c.id as string)
    );
    const list = [...fingerprints.values()]
      .filter((f) => f.retired && !continued.has(f.variableId))
      .map((f) => ({ id: f.variableId, header: f.currentHeader, summary: filingSummary(f) }));
    for (const l of editing?.legacyColumns ?? []) {
      if (!continued.has(l.id) && !list.some((c) => c.id === l.id)) {
        list.push({ id: l.id, header: l.name, summary: '' });
      }
    }
    return list;
  }, [fingerprints, editing?.columns, editing?.legacyColumns]);

  // Turning one on starts from its last header — the name it had, which is usually what people search for.
  const toggleHistorical = (id: string, header: string, on: boolean) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const rest = (cur.legacyColumns ?? []).filter((l) => l.id !== id);
      return { ...cur, legacyColumns: on ? [...rest, { id, name: header }] : rest };
    });
  };

  const renameHistorical = (id: string, name: string) => {
    setEditing((cur) =>
      cur
        ? { ...cur, legacyColumns: (cur.legacyColumns ?? []).map((l) => (l.id === id ? { ...l, name } : l)) }
        : cur
    );
  };

  const patchColumn = (index: number, changes: Partial<EditorColumn>) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const columns = cur.columns.map((c, i) => (i === index ? { ...c, ...changes } : c));
      return { ...cur, columns };
    });
  };

  // A type change to anything but Texto drops the accepted-value list with it: the backend only allows a
  // restriction on a text column, so carrying the values along would just fail the save.
  //
  // It also DETACHES the row. A column's value type is frozen once data has been filed against it, so a
  // continued column cannot change type — the backend refuses outright rather than quietly minting a
  // replacement and abandoning the values, which is what it used to do. Rather than block the edit, the
  // row becomes a new column and the one it was continuing falls into the retirement list, where the
  // preview reports exactly how many values stop being displayed. `detachedFrom` remembers the binding
  // so undoTypeChange can put it back.
  const changeColumnType = (index: number, type: string) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const columns = cur.columns.map((c, i) => {
        if (i !== index) {
          return c;
        }
        const values = type === 'text' ? {} : { acceptedValues: null };
        // A format the NEW type cannot render has to go, or the save is rejected. Dropped rather than
        // remembered: the two vocabularies do not overlap, so there is nothing to restore if the admin
        // types back — and silently holding an unusable value is what the backend refuses to store.
        const formats = formatsFor(type);
        const keepsFormat = c.format != null && c.format !== 'None' && formats.includes(c.format);
        const formatting = keepsFormat ? {} : { format: 'None' };
        // Only a CONTINUED column detaches, and only when the type actually moves. Re-picking the same
        // type, or retyping a row that is already new, must not manufacture a detachment.
        if (c.binding !== 'continue' || c.type === type) {
          return { ...c, type, ...values, ...formatting };
        }
        return {
          ...c,
          type,
          ...values,
          ...formatting,
          binding: 'new' as const,
          id: null,
          detachedFrom: { variableId: c.id ?? null, type: c.type },
        };
      });
      return { ...cur, columns };
    });
  };

  // Put back the binding a type change detached, restoring the original type with it. The pair of them
  // is what makes the detachment safe to trip over: it is one click out and one click back.
  const undoTypeChange = (index: number) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const columns = cur.columns.map((c, i) => {
        if (i !== index || !c.detachedFrom) {
          return c;
        }
        const { detachedFrom, ...rest } = c;
        return {
          ...rest,
          binding: 'continue' as const,
          id: detachedFrom.variableId,
          type: detachedFrom.type,
        };
      });
      return { ...cur, columns };
    });
  };

  // Accept the detachment: keep the new type, forget it was ever a continuation. Purely cosmetic — the
  // payload already reads 'new' — but it clears the notice so the row stops asking to be resolved.
  const keepAsNewColumn = (index: number) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const columns = cur.columns.map((c, i) => {
        if (i !== index) {
          return c;
        }
        const { detachedFrom, ...rest } = c;
        return rest;
      });
      return { ...cur, columns };
    });
  };

  // Point a row at a different stored column, or at none. This is the only other way a binding changes.
  const rebindColumn = (index: number, fingerprint: InventoryColumnFingerprint | null) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const columns = cur.columns.map((c, i) => {
        if (i !== index) {
          return c;
        }
        const { detachedFrom, ...rest } = c;
        return fingerprint
          ? {
              ...rest,
              binding: 'continue' as const,
              id: fingerprint.variableId,
              // Continuing a column means adopting its type — they cannot disagree, and silently
              // sending a mismatch would only come back as a 400.
              type: fingerprint.type,
              acceptedValues: fingerprint.type === 'text' ? rest.acceptedValues : null,
            }
          : { ...rest, binding: 'new' as const, id: null };
      });
      return { ...cur, columns };
    });
  };

  // What this row is bound to, stated under the header field and never hidden. The point of the whole
  // change: a header edit that takes someone else's data with it must LOOK like that while it is being
  // made, not only in a dialog at the end.
  // Whether this row has something the collapsed accordion header should flag — the same four
  // conditions renderBinding's alerts cover (stale binding, a duplicated name, a name that would strand a
  // retired column, or a type change that detached the row), computed the same way so the header's icon
  // never disagrees with what expanding the row shows.
  const columnHasWarning = (c: EditorColumn, i: number): boolean => {
    const fp = c.binding === 'continue' && c.id ? fingerprints.get(c.id) : undefined;
    const stale = fingerprintsReady && c.binding === 'continue' && !!c.id && !fp;
    const duplicated = !!c.name.trim() && dupeKeys.has(normalizeHeader(c.name));
    const claimedElsewhere = new Set(
      editing?.columns
        .filter((o, oi) => oi !== i && o.binding === 'continue' && o.id)
        .map((o) => o.id as string) ?? []
    );
    const strandable =
      fingerprintsReady && c.binding !== 'continue'
        ? retiredNameMatch(c.name, fingerprints, claimedElsewhere)
        : null;
    return stale || duplicated || !!strandable || !!c.detachedFrom;
  };

  // A column stays open while it has no name (freshly added, or loaded that way) unless the user has
  // toggled it by hand — see `columnOpenOverrides`.
  const isColumnOpen = (c: EditorColumn, i: number): boolean => columnOpenOverrides[i] ?? !c.name.trim();

  const toggleColumnOpen = (c: EditorColumn, i: number) => {
    setColumnOpenOverrides((cur) => ({ ...cur, [i]: !isColumnOpen(c, i) }));
  };

  const renderBinding = (c: EditorColumn, i: number) => {
    const fp = c.binding === 'continue' && c.id ? fingerprints.get(c.id) : undefined;
    // A binding whose column the server no longer knows about — a concurrent edit retired or removed it.
    // The one binding error a human can actually hit, so it blocks the save and offers a way out.
    //
    // Gated on fingerprintsReady, never on the map being non-empty: while a fetch is in flight the map
    // is empty (or, worse, another schedule's), and both would make every bound row accuse itself.
    const stale = fingerprintsReady && c.binding === 'continue' && !!c.id && !fp;
    const claimedElsewhere = new Set(
      editing?.columns
        .filter((o, oi) => oi !== i && o.binding === 'continue' && o.id)
        .map((o) => o.id as string) ?? []
    );
    const renaming = fp && (fp.currentHeader ?? '').trim() !== (c.name ?? '').trim();
    const duplicated = !!c.name.trim() && dupeKeys.has(normalizeHeader(c.name));
    // Only for a column that is NOT continuing anything — a row already pointed at a stored column is
    // not about to strand one. Gated on the load too: an unloaded map yields no match today, so this is
    // belt-and-braces, but every claim about the stored columns should depend on having them.
    const strandable =
      fingerprintsReady && c.binding !== 'continue'
        ? retiredNameMatch(c.name, fingerprints, claimedElsewhere)
        : null;

    return (
      <div
        className="binding-line small mt-1"
        // Only the row whose picker is open claims the ref — one picker is open at a time, and the
        // click-outside handler needs the trigger inside the boundary as well as the panel.
        ref={pickerFor === i ? pickerRef : undefined}
      >
        {/* Loud, and first. This is the one action that quietly loses a column's history, and it looks
            from the grid exactly like typing a name. It does NOT block: reusing a retired name really can
            be intentional. It just cannot be accidental. */}
        {strandable ? (
          <Alert color="danger" className="py-1 px-2 mb-1">
            <strong>
              {/* Aliased so the extracted message carries NAMED placeholders ({retiredHeader},
                  {retiredCount}) rather than positional {0}/{1} — see planBadge. */}
              {(() => {
                const retiredHeader = strandable.currentHeader;
                const retiredCount = strandable.dataPoints;
                return (
                  <Trans>
                    A retired column «{retiredHeader}» has this name and {retiredCount} filed values.
                  </Trans>
                );
              })()}
            </strong>{' '}
            <Trans>
              Saving as a new column leaves those values behind on the retired one, where they stop being
              displayed.
            </Trans>{' '}
            <Button
              size="sm"
              color="link"
              className="p-0"
              onClick={() => rebindColumn(i, strandable)}
            >
              <Trans>Restore it instead</Trans>
            </Button>
          </Alert>
        ) : null}

        {duplicated ? (
          <Alert color="danger" className="py-1 px-2 mb-1">
            <Trans>
              Another column in this layout has this name. Names must differ by more than case, accents
              or spacing — the saved view is named from them.
            </Trans>
          </Alert>
        ) : null}

        {c.detachedFrom ? (
          <Alert color="warning" className="py-1 px-2 mb-1">
            <Trans>
              The type changed, so this is now a new column and the one it continued will be retired.
            </Trans>{' '}
            <Button size="sm" color="link" className="p-0" onClick={() => undoTypeChange(i)}>
              <Trans>Undo type change</Trans>
            </Button>
            {' · '}
            <Button size="sm" color="link" className="p-0" onClick={() => keepAsNewColumn(i)}>
              <Trans>Keep as new column</Trans>
            </Button>
          </Alert>
        ) : null}

        {stale ? (
          <Alert color="danger" className="py-1 px-2 mb-1">
            <Trans>
              The column this continued no longer exists — someone else changed this schedule. Pick
              another, or make it a new column.
            </Trans>
          </Alert>
        ) : null}

        <span className="text-muted">
          {c.binding !== 'continue' ? (
            <Trans>New column · no data</Trans>
          ) : fp ? (
            <>
              {renaming ? (
                <Trans>
                  Renaming <b>{fp.currentHeader}</b> → <b>{c.name}</b>
                </Trans>
              ) : fp.retired ? (
                <Trans>
                  Restores <b>{fp.currentHeader}</b>
                </Trans>
              ) : (
                <Trans>
                  Continues <b>{fp.currentHeader}</b>
                </Trans>
              )}
              {` · ${filingSummary(fp)}`}
              {fp.formerHeaders.length > 0 ? (
                <div className="text-muted fst-italic">
                  <Trans>formerly:</Trans> {fp.formerHeaders.join(' · ')}
                </div>
              ) : null}
            </>
          ) : (
            <Trans>Continues an existing column</Trans>
          )}
          {'  '}
          <Button
            size="sm"
            color="link"
            className="p-0"
            onClick={() => {
              setPickerQuery('');
              setPickerFor(pickerFor === i ? null : i);
            }}
          >
            {/* "Change binding" reads as nonsense on a row that has not got one yet — which, now that
                adding a column opens this picker, is the first thing a new row shows. */}
            {c.binding === 'continue' ? (
              <Trans>Change binding…</Trans>
            ) : (
              <Trans>Choose an existing column…</Trans>
            )}
          </Button>
        </span>

        {pickerFor === i ? (
          <div className="border rounded p-2 mt-1" style={{ position: 'relative' }}>
            {/* Absolutely positioned against the panel, NOT laid out beside the heading. Every
                in-flow attempt — justify-content-between, margin-left:auto, w-100 — put it next to
                the text, because the flex row it lived in was only ever as wide as its own content.
                Taking it out of flow makes its position depend on the panel's box alone, which is the
                thing actually visible on screen. Also a bare <button>: the shared one carries
                .btn/.btn-sm, whose box is twice the height of its contents. */}
            <button
              type="button"
              aria-label={t`Close`}
              title={t`Close`}
              onClick={() => setPickerFor(null)}
              style={{
                position: 'absolute',
                top: '0.25rem',
                right: '0.5rem',
                background: 'none',
                border: 0,
                padding: 0,
                margin: 0,
                // `font` is a SHORTHAND and resets font-size, so fontSize has to come after it —
                // React writes these in key order, and the other way round the glyph stays tiny.
                font: 'inherit',
                fontSize: '1.5rem',
                lineHeight: 1,
                color: 'var(--bs-danger, #dc3545)',
                cursor: 'pointer',
              }}
            >
              ×
            </button>
            {/* Padded clear of the close button so a long heading cannot run under it. */}
            <div className="mb-2" style={{ paddingRight: '1.5rem' }}>
              <strong>
                <Trans>Which column is this?</Trans>
              </strong>
            </div>
            <Input
              className="custom mb-2"
              placeholder={t`Search by current or former name…`}
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
            />
            <div style={{ maxHeight: '16rem', overflowY: 'auto' }}>
              <Button
                size="sm"
                color="link"
                className="d-block p-0 mb-1"
                onClick={() => {
                  rebindColumn(i, null);
                  setPickerFor(null);
                }}
              >
                <Trans>New column — start with no data</Trans>
              </Button>
              {[...fingerprints.values()]
                .filter((f) => fingerprintMatches(f, pickerQuery))
                .map((f) => {
                  const taken = claimedElsewhere.has(f.variableId);
                  return (
                    <div key={f.variableId} className="mb-1">
                      <Button
                        size="sm"
                        color="link"
                        className="d-block text-start p-0"
                        disabled={taken}
                        onClick={() => {
                          rebindColumn(i, f);
                          setPickerFor(null);
                        }}
                      >
                        <b>{f.currentHeader}</b>
                        {` · ${typeLabel(f.type)} · ${filingSummary(f)}`}
                        {f.retired ? ` · ${t`retired`}` : ''}
                        {taken ? ` · ${t`already used by another column`}` : ''}
                      </Button>
                      {f.formerHeaders.length > 0 ? (
                        <div className="text-muted fst-italic ps-2">
                          <Trans>formerly:</Trans> {f.formerHeaders.join(' · ')}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  // Turning the restriction on seeds ONE blank value so there is somewhere to type; turning it off clears
  // the list (null = free text). Non-destructive either way — the column keeps its id, so its data stays.
  const toggleRestricted = (index: number, restricted: boolean) => {
    patchColumn(index, { acceptedValues: restricted ? [''] : null });
  };

  // Rewrite one column's accepted-value list from its CURRENT state (never from the render-time copy), so
  // two quick clicks on "Añadir valor" can't drop one — same reason moveColumn/addColumn compute inside the
  // updater.
  const patchValues = (index: number, next: (values: string[]) => string[]) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const columns = cur.columns.map((c, i) =>
        i === index ? { ...c, acceptedValues: next(c.acceptedValues ?? []) } : c
      );
      return { ...cur, columns };
    });
  };

  const patchValue = (index: number, valueIndex: number, value: string) => {
    patchValues(index, (values) => values.map((v, vi) => (vi === valueIndex ? value : v)));
  };

  const addValue = (index: number) => {
    patchValues(index, (values) => [...values, '']);
  };

  const removeValue = (index: number, valueIndex: number) => {
    patchValues(index, (values) => values.filter((_, vi) => vi !== valueIndex));
  };

  // Adding a column OPENS the picker on it. "What is this column?" is the first question about a new
  // row, not an afterthought reached through a second click — and adding one is precisely when a
  // retired column gets stranded, because the alternative to creating a fresh column is restoring one
  // that already holds data. Showing the choices here means that alternative is on screen at the
  // moment it applies, rather than being something the user has to already know to go looking for.
  //
  // It stays a choice, not a wizard: "New column — start with no data" is the first option in the
  // list, and clicking the trigger again just closes it.
  const addColumn = () => {
    setEditing((cur) =>
      cur ? { ...cur, columns: [...cur.columns, newColumn(cur.columns.length)] } : cur
    );
    // The row lands at the end, so its index is the length BEFORE the append. Read from `editing`
    // rather than computing inside the updater: opening a picker is a side effect, and an updater can
    // run twice under StrictMode.
    const index = editing ? editing.columns.length : 0;
    setPickerQuery('');
    setPickerFor(index);
    // Pin it open: left to the default, typing the first letter of its name would collapse it.
    setColumnOpenOverrides((cur) => ({ ...cur, [index]: true }));
  };

  const removeColumn = (index: number) => {
    setEditing((cur) =>
      cur ? { ...cur, columns: cur.columns.filter((_, i) => i !== index) } : cur
    );
  };

  const moveColumn = (index: number, delta: number) => {
    setEditing((cur) => {
      if (!cur) {
        return cur;
      }
      const target = index + delta;
      if (target < 0 || target >= cur.columns.length) {
        return cur;
      }
      const columns = [...cur.columns];
      [columns[index], columns[target]] = [columns[target], columns[index]];
      return { ...cur, columns };
    });
  };

  const validate = (def: InventorySchedule): string | null => {
    if (!def.fireId.trim()) {
      return t`The Fire ID is required.`;
    }
    if (!def.title.trim()) {
      return t`The title is required.`;
    }
    if (def.columns.length === 0) {
      return t`Define at least one column.`;
    }
    if (def.viewName && !/^[a-z0-9_]+$/.test(def.viewName)) {
      return t`The view name only allows lowercase letters, digits, and underscores.`;
    }
    if (def.viewName && def.viewName.length > MAX_VIEW_NAME_LENGTH) {
      return t`The view name can be at most ${MAX_VIEW_NAME_LENGTH} characters.`;
    }
    const seen = new Set<string>();
    // Stored column id -> the header of the row already continuing it, for the duplicate-binding check.
    const boundIds = new Map<string, string>();
    for (const c of def.columns) {
      if (!c.name.trim()) {
        return t`Every column needs a name.`;
      }
      // Aliased so each message below extracts a NAMED {columnName} placeholder — a bare ${c.name} is a
      // member expression, which Lingui can only name positionally ({0}), and the duplicate-value message
      // would then mix {0} with {v} and leave a translator guessing which is which.
      const columnName = c.name;
      // Normalized, not merely lowercased: the backend matches headers with accents and spacing ignored,
      // so 'Tipo de Interes' and 'Tipo de Interés' are the SAME column to it. Letting both through would
      // leave "which stored column is this?" to list order — reject it here as the backend does.
      const key = normalizeHeader(c.name);
      if (seen.has(key)) {
        return t`Duplicate column: «${columnName}». Names must differ by more than case, accents, or spacing.`;
      }
      seen.add(key);

      // A binding pointing at a column the server no longer has — someone else changed this schedule
      // while it was open. Caught here so the row can be re-pointed, rather than sent and 400'd.
      // Same gate as the inline warning: only meaningful once THIS schedule's columns have loaded.
      if (c.binding === 'continue' && c.id && fingerprintsReady && !fingerprints.has(c.id)) {
        return t`Column «${columnName}» continues a column that no longer exists. Re-point it, or make it a new column.`;
      }
      // Two rows continuing one stored column. The backend refuses rather than letting list order decide.
      if (c.binding === 'continue' && c.id) {
        if (boundIds.has(c.id)) {
          const other = boundIds.get(c.id) as string;
          return t`Columns «${other}» and «${columnName}» both continue the same stored column. A column can only be continued once.`;
        }
        boundIds.set(c.id, c.name);
      }

      if (!isRestricted(c)) {
        continue;
      }
      // Mirrors the backend's checks so the user is told here instead of via a 400: a restriction needs at
      // least one value, none of them blank, and no duplicates (compared case-insensitively, as the backend
      // collapses them).
      const values = (c.acceptedValues ?? []).map((v) => v.trim());
      if (values.length === 0) {
        return t`Define at least one accepted value for column «${columnName}», or turn off its value list.`;
      }
      if (values.some((v) => !v)) {
        return t`The accepted values of column «${columnName}» cannot be blank.`;
      }
      const seenValues = new Set<string>();
      for (const v of values) {
        if (seenValues.has(v.toLowerCase())) {
          return t`Duplicate accepted value in column «${columnName}»: «${v}».`;
        }
        seenValues.add(v.toLowerCase());
      }
    }

    // Historical columns — the same rules the backend applies, so they are reported here rather than as
    // a 400. Ones the layout continues are skipped: toPayload drops them (a revival makes them live).
    const continued = new Set(def.columns.filter((c) => c.binding === 'continue' && c.id).map((c) => c.id));
    const historicalKeys = new Set<string>();
    for (const l of def.legacyColumns ?? []) {
      if (continued.has(l.id)) {
        continue;
      }
      const historicalName = l.name.trim();
      if (!historicalName) {
        return t`Every historical column needs a name.`;
      }
      if (new TextEncoder().encode(historicalName).length > MAX_VIEW_COLUMN_NAME_BYTES) {
        return t`The historical column «${historicalName}» has a name that is too long for the view.`;
      }
      const key = normalizeHeader(historicalName);
      if (seen.has(key)) {
        return t`The historical column «${historicalName}» has the same name as a current column.`;
      }
      if (historicalKeys.has(key)) {
        return t`Two historical columns are named «${historicalName}».`;
      }
      historicalKeys.add(key);
    }
    return null;
  };

  const attemptSave = async () => {
    if (!editing || busyRef.current) {
      return;
    }
    const validationError = validate(editing);
    if (validationError) {
      setError(validationError);
      setNotice(null);
      return;
    }
    setError(null);
    // A brand-new schedule has no history to disturb, so there is nothing to confirm.
    if (isNew) {
      await doSave();
      return;
    }
    // Ask the BACKEND what this save will do, and gate on its verdict. Deliberately re-fetched here
    // rather than reusing the live `plan`: that one is debounced, so it can lag the final keystroke, and
    // gating a destructive save on a stale projection is how this went wrong the first time.
    if (!beginBusy()) {
      return;
    }
    let proceed = false;
    try {
      const fresh = await InventoryScheduleApi.preview(toPayload(editing));
      setPlan(fresh);
      // A view rename/drop always asks, even when the layout itself needs no confirmation: the backend's
      // plan only covers columns, and the Superset breakage happens outside anything it can see.
      if (fresh.requiresConfirmation || viewNameChange) {
        setConfirm({ kind: 'save', plan: fresh });
      } else {
        proceed = true;
      }
    } catch (e: any) {
      setError(e?.message ?? t`Could not determine the effects of this save.`);
    } finally {
      endBusy();
    }
    // AWAITED, and only after the preview's lock is released: doSave takes the lock itself, synchronously,
    // so no click can slip in between. This used to fire doSave un-awaited inside the try, and the finally
    // above then cleared `saving` while the upsert was still in flight — re-enabling Save mid-write.
    if (proceed) {
      await doSave();
    }
  };

  const doSave = async () => {
    if (!editing || !beginBusy()) {
      return;
    }
    setConfirm(null);
    setError(null);
    setNotice(null);
    let navigateTo: string | null = null;
    try {
      const saved = await InventoryScheduleApi.upsert(toPayload(editing));
      setNotice(t`Schedule saved.`);
      reload();
      // A create lands on '/new' — once it exists, its own URL is the fireId, so replace (not push) rather
      // than stacking a history entry the back button would only bounce off of.
      if (isNew) {
        navigateTo = `${basePath}/${encodeURIComponent(saved.fireId)}`;
      }
    } catch (e: any) {
      setError(e?.message ?? t`The schedule could not be saved.`);
    } finally {
      endBusy();
    }
    // After endBusy: the navigation block would otherwise refuse the save's own redirect.
    if (navigateTo) {
      history.replace(navigateTo);
    }
  };

  // The dialog stays put while its own write runs (the X, Escape, backdrop and Cancel all route here).
  const closeConfirm = () => {
    if (!busyRef.current) {
      setConfirm(null);
    }
  };

  const attemptRemove = () => {
    if (!editing || isNew || busyRef.current) {
      return;
    }
    setError(null);
    setConfirm({ kind: 'retire' });
  };

  const doRemove = async () => {
    if (!editing || isNew || !beginBusy()) {
      return;
    }
    setConfirm(null);
    setError(null);
    setNotice(null);
    let retired = false;
    try {
      await InventoryScheduleApi.remove(editing.fireId);
      setNotice(t`Schedule retired.`);
      reload();
      retired = true;
    } catch (e: any) {
      setError(e?.message ?? t`The schedule could not be retired.`);
    } finally {
      endBusy();
    }
    // After endBusy, for the same reason as doSave's redirect.
    if (retired) {
      history.push(basePath);
    }
  };

  // Subject of the retire confirmation, aliased so its message extracts a named {scheduleName}
  // placeholder rather than a positional one.
  const scheduleName = editing?.title || editing?.fireId || '';

  // The backend returns 503 { code: 'ANALYTICS_UNAVAILABLE' } when the Analytics DB isn't configured.
  // Inventory schedules then can't be read OR edited — short-circuit to a disabled notice with no tabs,
  // no "add", and no save/retire, rather than letting the UI pretend edits will stick.
  const analyticsUnavailable =
    !!loadError &&
    ((loadError as any).code === 'ANALYTICS_UNAVAILABLE' ||
      /Analytics DB is not configured/i.test(loadError.message ?? ''));

  if (analyticsUnavailable) {
    return (
      <div className={`inventory-schedule-manager h-full ${element?.[':className'] ?? ''}`}>
        <div className="static-markdown-blurb-container mb-3">
          <p>
            <Trans>
              This page defines the Inventory Schedules. These schedules are uploaded as CSV or TXT files
              in the institutions' financial reports.
            </Trans>
          </p>
        </div>
        <Alert color="warning">
          <Trans>
            Editing Inventory Schedules is unavailable: the analytics database is not configured in this
            environment.
          </Trans>
        </Alert>
      </div>
    );
  }

  return loading ? (
    <InventoryScheduleLoadingSkeleton />
  ) : (
    <div className={`inventory-schedule-manager ${element?.[':className'] ?? ''}`}>
      {loadError ? (
        <Alert color="danger">
          <Trans>The schedules could not be loaded: {loadError.message}</Trans>
        </Alert>
      ) : null}

      {error ? <Alert color="danger">{error}</Alert> : null}
      {notice ? <Alert color="success">{notice}</Alert> : null}

      {!routeId ? (
        // The list: styled and structured like a flat (ui:asCard === false) worktray — same header,
        // description placement, and sticky-header table classes — even though a real WorkTray can't be
        // used here (it wants a `resource` to fetch from and a columns/actions schema; this list is
        // fixed-shape and comes from InventoryScheduleApi.list(), already loaded above).
        <div className="worktray worktray--flat">
          {/* flex-wrap: on a narrow screen the title and the button can't share a row, so the button
              drops to its own line below rather than squeezing both into an unreadable strip. */}
          <div className="worktray-flat-header flex-wrap" style={{ rowGap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 0, gap: 16 }}>
              <div className="title has-buttons">
                <h5 className="mb-0">{element?.title ?? t`Inventory Schedules`}</h5>
              </div>
            </div>
            <Button color="info" size="sm" onClick={addSchedule}>
              <PiPlusDuotone size={16} /> <Trans>Add Schedule</Trans>
            </Button>
          </div>

          <div className="worktray-description" style={{ position: 'relative', bottom: '15px' }}>
            <small>
              <Trans>
                This page defines the Inventory Schedules. These schedules are uploaded as CSV or TXT
                files in the institutions' financial reports.
              </Trans>{' '}
              <Trans>
                Give the schedule a name or title, and define the columns it must include to be filled
                in.
              </Trans>
            </small>
          </div>

          {/* Below md, a table just gets clipped or forces horizontal scrolling — a stacked card per
              schedule reads far better on a phone. Both are rendered; only one is ever visible. */}
          <div className="worktray-flat-scroll">
            <div
              className={classnames('worktray-table-container hidden md:block', {
                'full-height': list.length === 0,
              })}
            >
              <Table striped borderless hover>
                <thead>
                  <tr style={{ textAlign: 'left'}}>
                    <th>Fire ID</th>
                    <th>
                      <Trans>Name</Trans>
                    </th>
                    <th>
                      <Trans># Columns</Trans>
                    </th>
                    <th aria-label={t`Actions`} />
                  </tr>
                </thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.fireId}>
                      <td>{s.fireId}</td>
                      <td>{s.title || s.fireId}</td>
                      <td>{s.columns?.length ?? 0}</td>
                      <td className="action-buttons-cell">
                        <Button size="sm" color="secondary" onClick={() => selectSchedule(s)}>
                          <Trans>Edit</Trans>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <EmptyState message={t`No inventory schedules defined yet.`} size={150} />
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>

            {/* `md:hidden` (Tailwind) has to live on its OWN element here, never alongside a bootstrap
                `d-flex`/`d-*` class: bootstrap's display utilities carry `!important`, which would beat
                Tailwind's `display:none` at the md breakpoint and leave this list visible next to the
                table instead of replacing it. */}
            <div className="md:hidden">
              <div className="d-flex flex-column" style={{ gap: '0.75rem' }}>
                {list.map((s) => (
                  <div
                    key={s.fireId}
                    className="border rounded d-flex align-items-center justify-content-between"
                    style={{ padding: '0.75rem' }}
                  >
                    <div className="min-w-0">
                      <div className="fw-bold truncate">{s.title || s.fireId}</div>
                      <div className="form-text truncate">
                        {s.fireId}
                        {' · '}
                        {(() => {
                          const columnCount = s.columns?.length ?? 0;
                          return <Trans>{columnCount} columns</Trans>;
                        })()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      color="secondary"
                      className="shrink-0"
                      style={{ marginLeft: '0.75rem' }}
                      onClick={() => selectSchedule(s)}
                    >
                      <Trans>Edit</Trans>
                    </Button>
                  </div>
                ))}
                {list.length === 0 ? (
                  <EmptyState message={t`No inventory schedules defined yet.`} size={150} />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <fieldset className="field field-array field-array-of-object">
          <legend className="type-section form-subtitle">
            {element?.title ?? t`Inventory Schedules`}
          </legend>

          {editing ? (
          <div className="tab-pane-item array-item pt-3">
            <Button color="link" className="p-0 mb-2 d-inline-flex align-items-center" style={{ gap: '0.25rem' }} onClick={goToList}>
              <PiArrowLeftDuotone size={14} /> <Trans>Back to schedules</Trans>
            </Button>

                {/* Schedule title */}
                <div className="schema-field rjsf-field rjsf-field-string">
                  <label className="control-label">
                    <Trans>Schedule Title</Trans>
                  </label>
                  <div className="mb-3 input-wrapper">
                    <Input
                      className="custom"
                      value={editing.title}
                      onChange={(e) => patch({ title: e.target.value })}
                    />
                  </div>
                </div>

                {/* Fire ID */}
                <div className="schema-field rjsf-field rjsf-field-string">
                  <label className="control-label">Fire ID</label>
                  <div className="mb-3 input-wrapper">
                    <Input
                      className="custom"
                      value={editing.fireId}
                      disabled={!isNew} // identity — fixed once the schedule exists
                      onChange={(e) => patch({ fireId: e.target.value })}
                    />
                  </div>
                </div>

                {/* View name — names the analytics view (ocif_analytics_<name>). Left empty, no view is
                    created (and an existing one is dropped on save). */}
                <div className="schema-field rjsf-field rjsf-field-string">
                  <label className="control-label">
                    <Trans>View name</Trans>
                  </label>
                  <div className="mb-3 input-wrapper">
                    <Input
                      className="custom"
                      value={editing.viewName ?? ''}
                      maxLength={MAX_VIEW_NAME_LENGTH}
                      placeholder={t`empty = no view is created`}
                      onChange={(e) => patch({ viewName: e.target.value })}
                    />
                  </div>
                  {viewNameChange ? <ViewNameChangeWarning change={viewNameChange} /> : null}
                </div>

                {/* Columns */}
                <fieldset className="field field-array field-array-of-object">
                  <legend className="type-section form-subtitle">
                    <Trans>Columns</Trans>
                  </legend>

                  {/* The binding picker's counterpart at SCHEDULE scope. Same shape deliberately: a
                      list of candidates, each identified by what it holds and what choosing it would
                      cost, rather than by an identifier nobody can place. A timestamp on its own is
                      exactly as meaningless as a VariableId.

                      Restoring here only LOADS the old layout into the editor. The save that follows
                      is an ordinary save, which is why the restore itself becomes a version in the
                      history — the same code records it as records everything else. */}
                  {!isNew ? (
                    <div className="mb-2">
                      <Button
                        size="sm"
                        color="secondary"
                        outline
                        onClick={() => setHistoryOpen((o) => !o)}
                      >
                        <Trans>Restore a previous layout…</Trans>
                      </Button>

                      {historyOpen ? (
                        <div className="border rounded p-2 mt-1" style={{ position: 'relative' }}>
                          <button
                            type="button"
                            aria-label={t`Close`}
                            title={t`Close`}
                            onClick={() => setHistoryOpen(false)}
                            style={{
                              position: 'absolute',
                              top: '0.25rem',
                              right: '0.5rem',
                              background: 'none',
                              border: 0,
                              padding: 0,
                              margin: 0,
                              font: 'inherit',
                              fontSize: '1.5rem',
                              lineHeight: 1,
                              color: 'var(--bs-danger, #dc3545)',
                              cursor: 'pointer',
                            }}
                          >
                            ×
                          </button>
                          <div className="mb-2" style={{ paddingRight: '1.5rem' }}>
                            <strong>
                              <Trans>Restore which layout?</Trans>
                            </strong>
                          </div>

                          {versionsError ? (
                            <Alert color="danger" className="py-1 px-2 mb-1">
                              {versionsError}
                            </Alert>
                          ) : null}

                          {versions === null && !versionsError ? (
                            <div className="form-text">
                              <Spinner size="sm" /> <Trans>Loading history…</Trans>
                            </div>
                          ) : null}

                          {versions !== null && versions.length === 0 ? (
                            <div className="form-text">
                              <Trans>
                                No recorded history for this schedule yet. Versions are written from the
                                first save made through this editor.
                              </Trans>
                            </div>
                          ) : null}

                          <div style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                            {(versions ?? []).map((v) => {
                              const delta = versionDelta(v);
                              return (
                                <div key={v.at} className="mb-2">
                                  <Button
                                    size="sm"
                                    color="link"
                                    className="d-block text-start p-0"
                                    disabled={v.isCurrent}
                                    onClick={() => loadVersion(v)}
                                  >
                                    <b>{formatVersionStamp(v.at)}</b>
                                    {v.changedBy ? ` · ${v.changedBy}` : ''}
                                    {` · `}
                                    {(() => {
                                      const columnCount = v.columns;
                                      return <Trans>{columnCount} columns</Trans>;
                                    })()}
                                    {v.isCurrent ? ` · ${t`current`}` : ''}
                                  </Button>
                                  {/* The delta is the whole reason a restore point is choosable —
                                      without it every row reads as an interchangeable date. */}
                                  {!v.isCurrent && delta ? (
                                    <div className="form-text ps-2">
                                      <Trans>Restoring:</Trans> {delta}
                                    </div>
                                  ) : null}
                                  {!v.isCurrent && !delta ? (
                                    <div className="form-text ps-2">
                                      <Trans>Identical to the current layout.</Trans>
                                    </div>
                                  ) : null}
                                  <div className="form-text fst-italic ps-2">
                                    {v.columnHeaders.join(' · ')}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="d-flex flex-column" style={{ gap: '1.25rem' }}>
                    {editing.columns.map((c, i) => {
                      const open = isColumnOpen(c, i);
                      const warning = columnHasWarning(c, i);
                      const p = plan?.columns?.[i];
                      const badge = !isNew && p ? planBadge(p) : null;

                      return (
                        <AccordionItem
                          key={i}
                          itemId={String(i)}
                          className='my-4'
                          isOpen={open}
                          toggle={() => toggleColumnOpen(c, i)}
                          title={
                            // Tailwind utilities throughout, matching the one AccordionItem title in this
                            // app already known to lay out a truncating name plus a pinned action group
                            // correctly (RepeatingRows.tsx) — mixing in bootstrap's `d-flex` here is what
                            // produced the broken, wrapped-onto-its-own-line header this replaces.
                            //
                            // flex-wrap (dropped again at sm+) is the mobile fallback: name+badges, the
                            // status text, and 3 icon buttons together can outgrow a phone-width row, and
                            // wrapping the status+controls onto their own line beats forcing the whole
                            // page to scroll horizontally.
                            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 min-w-0" style={{ width: '100%' }}>
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <strong className="truncate">
                                  {c.name.trim() || t`New column ${i + 1}`}
                                </strong>
                                <span className="badge bg-secondary shrink-0">{typeLabel(c.type)}</span>
                                {warning ? (
                                  <PiWarningDuotone
                                    size={14}
                                    className="text-danger shrink-0"
                                    aria-label={t`This column needs attention`}
                                  />
                                ) : null}
                              </div>
                              {/* The on-save status reads as plain bold text rather than a badge, right
                                  next to the controls — a second pill next to the name competed with it
                                  for attention instead of reading as a status. */}
                              {badge ? (
                                <span
                                  className={`fw-bold text-${badge.color} shrink-0`}
                                  title={badge.title}
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  <small><em>{badge.label}</em></small>
                                </span>
                              ) : null}
                              {/* Reorder/remove stay reachable without expanding the row — stopPropagation
                                  keeps them from also toggling the accordion they sit inside. */}
                              <div
                                className="flex items-center gap-1 shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconButton
                                  label={t`Move up`}
                                  disabled={i === 0}
                                  onClick={() => moveColumn(i, -1)}
                                >
                                  <PiArrowUpDuotone size={16} />
                                </IconButton>
                                <IconButton
                                  label={t`Move down`}
                                  disabled={i === editing.columns.length - 1}
                                  onClick={() => moveColumn(i, 1)}
                                >
                                  <PiArrowDownDuotone size={16} />
                                </IconButton>
                                <IconButton
                                  label={t`Remove`}
                                  color="var(--bs-danger, #dc3545)"
                                  onClick={() => removeColumn(i)}
                                >
                                  <PiXDuotone size={16} />
                                </IconButton>
                              </div>
                            </div>
                          }
                        >
                          <div className="p-3 pt-3">
                            {/* Name and type share a row once there's room for it (md+); type is capped
                                to a third of the row since it never needs more than a short dropdown's
                                width. Stacked full-width below that, where a third of the row would be
                                too cramped to use. */}
                            <div className="flex flex-col md:flex-row" style={{ gap: '1rem' }}>
                              <div className="flex-1 min-w-0">
                                <FieldLabel>
                                  <Trans>Column name</Trans>
                                </FieldLabel>
                                <div className="input-wrapper">
                                  <Input
                                    className="custom"
                                    value={c.name}
                                    onChange={(e) => patchColumn(i, { name: e.target.value })}
                                  />
                                </div>
                                <div>
                                  {renderBinding(c, i)}
                                </div>
                              </div>

                              <div className="md:w-1/3">
                                <FieldLabel>
                                  <Trans>Column type</Trans>
                                </FieldLabel>
                                <div className="input-wrapper">
                                  <Input
                                    type="select"
                                    value={c.type ?? ''}
                                    onChange={(e) => changeColumnType(i, e.target.value)}
                                  >
                                    <option disabled value="">
                                      ---
                                    </option>
                                    {TYPE_VALUES.map((value) => (
                                      <option key={value} value={value}>
                                        {typeLabel(value)}
                                      </option>
                                    ))}
                                  </Input>
                                </div>
                                {/* How the download template presents the column — offered only where the
                                    type has more than one sensible presentation (currency over a numeric
                                    type, the three date layouts). Purely presentational: unlike a type
                                    change this keeps the column's id and all of its data. */}
                                {formatsFor(c.type).length > 0 ? (
                                  <div className="mt-2">
                                    <FieldLabel>
                                      <Trans>Template format</Trans>
                                    </FieldLabel>
                                    <div className="input-wrapper">
                                      <Input
                                        type="select"
                                        value={c.format ?? 'None'}
                                        onChange={(e) => patchColumn(i, { format: e.target.value })}
                                      >
                                        {['None', ...formatsFor(c.type)].map((value) => (
                                          <option key={value} value={value}>
                                            {formatLabel(value)}
                                          </option>
                                        ))}
                                      </Input>
                                    </div>
                                  </div>
                                ) : null}
                                {/* Only a text column can carry a restriction — sits right under the
                                    dropdown that decides whether it's offered at all. */}
                                {c.type === 'text' ? (
                                  // `form-check` is what gives form-check-input's -1.5em margin
                                  // somewhere to sit — without it the checkbox has nothing to its left
                                  // and renders clipped against the edge of the accordion body.
                                  <label className="form-check d-flex align-items-center mt-2" style={{ gap: '0.5rem' }}>
                                    <Input
                                      type="checkbox"
                                      checked={isRestricted(c)}
                                      onChange={(e) => toggleRestricted(i, e.target.checked)}
                                    />
                                    <span>
                                      <Trans>Value list</Trans>
                                    </span>
                                  </label>
                                ) : null}
                              </div>
                            </div>

                            {/* Accepted values — only meaningful for a text column with the value list
                                turned on (the backend rejects a restriction on any other type). Editing
                                the list never affects the column's existing data. */}
                            {c.type === 'text' && isRestricted(c) ? (
                              <div className="mt-4">
                                <FieldLabel>
                                  <Trans>Accepted values</Trans>
                                </FieldLabel>
                                {/* A GRID, not a flow-wrap: 3 even columns on md+ (1 below it) so values
                                    line up in readable rows instead of packing left-to-right by however
                                    wide each token happens to be. Each cell's input fills the column, so
                                    no per-input width is needed any more. */}
                                <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '0.5rem' }}>
                                  {(c.acceptedValues ?? []).map((v, vi) => (
                                    // No .input-wrapper here — it's block-level with no rule in this
                                    // repo (it comes from the UWE library), which pushed the remove
                                    // button onto its own line under the field instead of beside it.
                                    <div key={vi} className="flex items-center min-w-0" style={{ gap: '0.25rem' }}>
                                      <Input
                                        bsSize="sm"
                                        className="custom"
                                        style={{ minWidth: 0 }}
                                        value={v}
                                        placeholder={t`Value ${vi + 1}`}
                                        onChange={(e) => patchValue(i, vi, e.target.value)}
                                      />
                                      <IconButton
                                        label={t`Remove value ${vi + 1}`}
                                        color="var(--bs-danger, #dc3545)"
                                        onClick={() => removeValue(i, vi)}
                                      >
                                        <PiXDuotone size={14} />
                                      </IconButton>
                                    </div>
                                  ))}
                                </div>
                                {/* Count and Add share ONE line. The count has to sit outside the scroll
                                    box — once the list is capped you cannot tell how many values there
                                    are by looking at it — but giving it a line of its own spent height on
                                    a single short number. */}
                                <div className="d-flex align-items-center mt-3" style={{ gap: '0.5rem' }}>
                                  <Button
                                    type="button"
                                    color="info"
                                    size="sm"
                                    className="array-item-add"
                                    onClick={() => addValue(i)}
                                  >
                                    <PiPlusDuotone size={14} /> <Trans>Add value</Trans>
                                  </Button>
                                  <span className="form-text" style={{ paddingLeft: '0.5rem' }}>
                                    {(() => {
                                      const valueCount = (c.acceptedValues ?? []).length;
                                      return <Trans>{valueCount} values</Trans>;
                                    })()}
                                  </span>
                                </div>
                              </div>
                            ) : null}

                            {/* The consequence, per column, while it is being edited — the affordance that
                                was missing when CML's layout was rewritten in place. `plan.columns` is
                                positional, matching the payload we just sent. The badge itself is already
                                shown on the collapsed header; this is the detail behind it. */}
                            {!isNew ? (
                              <div className="mt-4">
                                <FieldLabel>
                                  <Trans>On save</Trans>
                                </FieldLabel>
                                {(() => {
                                  if (!p) {
                                    // A bare dash was indistinguishable from "nothing happens". The
                                    // preview is skipped while a header is blank and 400s on a duplicate,
                                    // and in both cases every badge silently emptied — right when the
                                    // layout is at its least trustworthy. Say which it is.
                                    const blank = (editing?.columns ?? []).some((x) => !x.name.trim());
                                    const dupe = dupeKeys.size > 0;
                                    return (
                                      <div className="form-text">
                                        {blank || dupe ? (
                                          <Trans>
                                            Effects unavailable until the column names are valid
                                          </Trans>
                                        ) : (
                                          '—'
                                        )}
                                      </div>
                                    );
                                  }
                                  // Aliased for NAMED message placeholders — see planBadge.
                                  const count = p.dataPoints;
                                  const fromHeader = p.fromHeader ?? '';
                                  return (
                                    <div>
                                      {p.dataPoints > 0 ? (
                                        <Trans>{count} filed values</Trans>
                                      ) : (
                                        <Trans>No data</Trans>
                                      )}
                                      {p.fromHeader && p.fromHeader !== p.name ? (
                                        <div className="form-text">
                                          <Trans>was «{fromHeader}»</Trans>
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : null}
                          </div>
                        </AccordionItem>
                      );
                    })}
                    <div>
                      <Button
                        type="button"
                        color="info"
                        size="sm"
                        className="array-item-add"
                        onClick={addColumn}
                      >
                        <PiPlusDuotone size={16} /> <Trans>Add column</Trans>
                      </Button>
                    </div>
                  </div>
                </fieldset>

                {/* Historical columns: RETIRED columns the generated view keeps showing, after every live
                    one, under a name chosen here. They stay out of templates and of the layout above — this
                    is only about what the view (and so Superset) can still read. */}
                {!isNew && historicalCandidates.length > 0 ? (
                  <fieldset className="field field-array mt-3">
                    <legend className="type-section form-subtitle">
                      <Trans>Historical columns in the view</Trans>
                    </legend>
                    <p className="form-text">
                      <Trans>
                        Retired columns are hidden from the view. Turn one on to keep showing the data filed
                        under it, as an extra column after the current ones. It does not come back to the
                        template.
                      </Trans>
                    </p>
                    {historicalCandidates.map((candidate) => {
                      const shown = (editing.legacyColumns ?? []).find((l) => l.id === candidate.id);
                      return (
                        <div key={candidate.id} className="historical-column mb-2">
                          <label className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!shown}
                              onChange={(e) => toggleHistorical(candidate.id, candidate.header, e.target.checked)}
                            />
                            <span>
                              <b>{candidate.header}</b>
                              {candidate.summary ? ` · ${candidate.summary}` : ''}
                            </span>
                          </label>
                          {shown ? (
                            <div className="input-wrapper ms-4 mt-1">
                              <Input
                                className="custom"
                                value={shown.name}
                                placeholder={t`Column name in the view`}
                                aria-label={t`Column name in the view`}
                                onChange={(e) => renameHistorical(candidate.id, e.target.value)}
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </fieldset>
                ) : null}

                {/* Save / delete toolbar (the legacy workpage saved via the page frame; the dedicated
                    controller needs explicit actions). */}
                <div className="flex my-3 align-right gap-2 justify-end">
                  {!isNew ? (
                    <Button color="danger" onClick={attemptRemove} disabled={saving}>
                      <Trans>Retire</Trans>
                    </Button>
                  ) : null}
                  <Button color="primary" onClick={attemptSave} disabled={saving}>
                    {saving ? <Spinner size="sm" /> : <Trans>Save</Trans>}
                  </Button>
                </div>
              </div>
        ) : (
          <Alert color="warning">
            <Trans>This schedule could not be found. It may have been retired or removed.</Trans>{' '}
            <Button size="sm" color="link" className="p-0" onClick={goToList}>
              <Trans>Back to schedules</Trans>
            </Button>
          </Alert>
          )}
        </fieldset>
      )}

      {/* Consequence confirmation — the last look before a save that relabels or removes data, or
          before a retire. Every figure here comes from the backend plan, computed from the bindings
          the rows declared, so the dialog cannot disagree with what the save does. */}
      <Modal isOpen={confirm != null} toggle={closeConfirm}>
          {confirm?.kind === 'save' ? (
            <>
              <ModalHeader toggle={closeConfirm}>
                <Trans>Confirm changes</Trans>
              </ModalHeader>
              <ModalBody>
                {/* The view rename leads even the takeovers: those are spelled out with counts and can be
                    undone from the history, while a broken Superset dataset is not visible from here at all. */}
                {viewNameChange ? <ViewNameChangeWarning change={viewNameChange} /> : null}
                {/* TAKEOVERS FIRST, and loudest. A rename whose Variable already carries filed data does
                    not move a column — it relabels history that was filed to mean something else. This is
                    the case that silently scrambled CML, so it leads, names both headers, and states the
                    row count. */}
                {(() => {
                  const takeovers = confirm.plan.columns.filter(
                    (c) => c.warning === 'CARRIES_UNRELATED_HISTORY'
                  );
                  return takeovers.length > 0 ? (
                    <Alert color="danger">
                      <strong>
                        <Trans>Existing data will be relabeled</Trans>
                      </strong>
                      <ul className="mb-0 mt-2">
                        {takeovers.map((c, i) => {
                          const from = c.fromHeader ?? '';
                          const to = c.name;
                          const count = c.dataPoints;
                          return (
                            <li key={`to-${i}`}>
                              <Trans>
                                {count} values filed as «{from}» will from now on be displayed as «{to}».
                                Only do this if they really are the same column.
                              </Trans>
                            </li>
                          );
                        })}
                      </ul>
                    </Alert>
                  ) : null;
                })()}

                {/* A type change no longer has a section of its own. It is not a distinct outcome any
                    more: the row detaches to a NEW column and the column it used to continue simply
                    turns up under "Removed columns" with its value count, which says the same thing
                    once instead of twice. */}

                {confirm.plan.retirements.length > 0 ? (
                  <>
                    <strong>
                      <Trans>Removed columns</Trans>
                    </strong>
                    <ul>
                      {/* One whole-sentence message per branch rather than a ternary nested inside a
                          single Trans: a translator gets a complete sentence to work with, and the
                          how-to-undo clause stays attached to the case it belongs to.

                          These used to promise that "re-adding a column with the same name and type"
                          brings it back. That was true while the header decided identity, and is now
                          false: retyping the old name produces a NEW, empty column. Bringing one back is
                          an explicit act — point a row at it with "Change binding…" — so that is what
                          these say. */}
                      {confirm.plan.retirements.map((r, i) => {
                        const name = r.name;
                        const count = r.dataPoints;
                        return (
                          <li key={`rm-${i}`}>
                            {r.dataPoints > 0 ? (
                              <Trans>
                                «{name}» <strong>has {count} filed values</strong>: they are kept in
                                analytics but are no longer displayed. To bring it back later, use
                                «Change binding…» on a column and pick it — retyping its name only
                                creates a new, empty column.
                              </Trans>
                            ) : (
                              <Trans>
                                «{name}» is removed. To bring it back later, use «Change binding…» on a
                                column and pick it — retyping its name only creates a new, empty column.
                              </Trans>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : null}

                {(() => {
                  const revived = confirm.plan.columns.filter((c) => c.action === 'revive');
                  return revived.length > 0 ? (
                    <Alert color="success">
                      <Trans>
                        Restored (they come back with their data):{' '}
                        {revived
                          .map((c) => `«${c.name}» (${c.dataPoints})`)
                          .join(', ')}
                        .
                      </Trans>
                    </Alert>
                  ) : null;
                })()}

                {(() => {
                  const renames = confirm.plan.columns.filter(
                    (c) => c.action === 'rename' && !c.warning
                  );
                  return renames.length > 0 ? (
                    <small className="text-muted">
                      <Trans>
                        Renamed (no data to move):{' '}
                        {renames.map((c) => `«${c.fromHeader ?? ''}» → «${c.name}»`).join(', ')}.
                      </Trans>
                    </small>
                  ) : null;
                })()}
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" outline onClick={closeConfirm} disabled={saving}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={doSave} disabled={saving}>
                  <Trans>Save changes</Trans>
                </Button>
              </ModalFooter>
            </>
          ) : null}
          {confirm?.kind === 'retire' ? (
            <>
              <ModalHeader toggle={closeConfirm}>
                <Trans>Retire schedule</Trans>
              </ModalHeader>
              <ModalBody>
                <p>
                  <Trans>
                    «{scheduleName}» will be retired: it is hidden from the module and from the
                    institutions' filings.
                  </Trans>
                </p>
                {editing?.hasData ? (
                  <p>
                    <Trans>
                      <strong>This schedule has filed data.</strong> It is kept in analytics and stays
                      available through the API — it is only hidden.
                    </Trans>
                  </p>
                ) : null}
                <small className="text-muted">
                  <Trans>It is reversible: saving the schedule again reactivates it.</Trans>
                </small>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" outline onClick={closeConfirm} disabled={saving}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button color="danger" onClick={doRemove} disabled={saving}>
                  <Trans>Retire</Trans>
                </Button>
              </ModalFooter>
            </>
          ) : null}
        </Modal>

      {/* The whole app, not just this editor, is off-limits while a save or retire is in flight: an edit
          typed mid-save would be wiped by the reload that follows it, and anything that navigates away
          invites a second write on top of the first. */}
      <BlockingOverlay open={saving}>
        <Trans>Saving changes… Please wait and do not close this page.</Trans>
      </BlockingOverlay>
    </div>
  );
}

export default InventoryScheduleManager;
